/**
 * Expected business preconditions from `workJourney.createMatrixAttendance`.
 * Mapped to user-facing copy in the matrix UI; not actionable Sentry alerts.
 */

export const MATRIX_CREATE_OTHER_JOURNEY_MESSAGE =
  'User already has an attendance record for another journey on this day';

export const MATRIX_CREATE_NOT_A_MEMBER_MESSAGE =
  'User is not a member of this work journey';

export type WorkJourneyMatrixCreateErrorKind = 'other_journey' | 'not_a_member' | 'unknown';

const codeEndsWith = (code: string, suffix: string): boolean =>
  code === suffix || code.endsWith(`/${suffix}`);

const getErrorParts = (error: unknown): { code: string; message: string } => {
  if (!error || typeof error !== 'object') {
    return { code: '', message: '' };
  }
  const maybe = error as { code?: unknown; message?: unknown };
  return {
    code: typeof maybe.code === 'string' ? maybe.code : '',
    message: typeof maybe.message === 'string' ? maybe.message : '',
  };
};

const isFailedPreconditionOrEmpty = (code: string): boolean =>
  !code || codeEndsWith(code, 'failed-precondition');

const isOtherJourneyMessage = (message: string): boolean =>
  message.includes(MATRIX_CREATE_OTHER_JOURNEY_MESSAGE) ||
  message.includes('another journey') ||
  message.includes('OTHER_JOURNEY');

const isNotAMemberMessage = (message: string): boolean =>
  message.includes(MATRIX_CREATE_NOT_A_MEMBER_MESSAGE) ||
  message.includes('NOT_A_MEMBER') ||
  message.includes('not a member of this work journey');

export const resolveWorkJourneyMatrixCreateErrorKind = (
  error: unknown,
): WorkJourneyMatrixCreateErrorKind => {
  const { code, message } = getErrorParts(error);
  if (!message) {
    return 'unknown';
  }

  if (isOtherJourneyMessage(message) && isFailedPreconditionOrEmpty(code)) {
    return 'other_journey';
  }

  if (isNotAMemberMessage(message) && isFailedPreconditionOrEmpty(code)) {
    return 'not_a_member';
  }

  return 'unknown';
};

export const isExpectedWorkJourneyMatrixCreateError = (error: unknown): boolean =>
  resolveWorkJourneyMatrixCreateErrorKind(error) !== 'unknown';

/** i18n id for matrix createPastDay error feedback. */
export const getMatrixCreatePastDayErrorMessageId = (error: unknown): string => {
  const kind = resolveWorkJourneyMatrixCreateErrorKind(error);
  if (kind === 'other_journey') {
    return 'workJourney.management.matrix.createPastDay.otherJourney';
  }
  if (kind === 'not_a_member') {
    return 'workJourney.management.matrix.createPastDay.notAMember';
  }
  return 'workJourney.management.matrix.createPastDay.error';
};
